/**
 * Pure helpers for discovering the `$param` references inside a fragment's
 * `steps` JSON.
 *
 * Fragment steps reference their parameters as `{ "$param": "name" }` objects
 * nested arbitrarily deep inside step args (predicates, aggregations,
 * derivations, ...). This differs from composite pipeline cards, whose
 * runtime params are predicate leaves shaped like `{ field, op, param }`
 * (see `pipelineParams.helpers.ts` / `extractPipelineParamNames`), so the
 * two walkers key on different property names.
 */

/**
 * Recursively walk an arbitrary JSON value, invoking `onParam` with the
 * string value of every `$param` property encountered on an object.
 *
 * A `$param` property is only recorded when its value is a string — a
 * fragment param ref always names its param with a string, and this guards
 * against an unrelated object that happens to carry a non-string `$param`
 * key.
 */
const walkForFragmentParams = (
  value: unknown,
  onParam: (name: string) => void,
): void => {
  if (Array.isArray(value)) {
    for (const item of value) {
      walkForFragmentParams(item, onParam);
    }
    return;
  }
  if (value !== null && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const paramRef = record.$param;
    if (typeof paramRef === 'string') {
      onParam(paramRef);
    }
    for (const key of Object.keys(record)) {
      walkForFragmentParams(record[key], onParam);
    }
  }
};

/**
 * Collect the names of every `$param` a fragment's steps reference.
 *
 * Walks the whole steps array (all nested arrays/objects at any depth)
 * collecting the string value of each `$param` property.
 *
 * @param steps - The fragment's step dicts (opaque JSON).
 * @returns Param names, de-duplicated, in stable first-seen (walk) order.
 */
export const extractFragmentParamNames = (steps: unknown[]): string[] => {
  const seen = new Set<string>();
  const names: string[] = [];
  walkForFragmentParams(steps, (name) => {
    if (!seen.has(name)) {
      seen.add(name);
      names.push(name);
    }
  });
  return names;
};
